/**
 * Build per-respondent pre×post scatter points for one MSI effect.
 *
 * Records come from useHierarchyData (questionnaire responses enriched with
 * MomentPosition). FormID is per submission and therefore NOT stable across
 * measure moments — cross-moment pairing keys on Persona by default, so only
 * persona-linked respondents can appear in this scatter.
 * Pure function — no Firebase imports.
 *
 * AI-CONTEXT: See docs/pitfalls.md#database-totaldocs-overcounts-respondents
 * AI-CONTEXT: See docs/pitfalls.md#database-raw-input-skips-negative-mirroring
 * (scores go through _mirroredScore, never raw Input averaging)
 */

/**
 * @typedef {object} ScatterPoint
 * @property {string} respondent - The respondent key value
 * @property {number} x - Mean score at the respondent's first measured moment
 * @property {number} y - Mean score at the respondent's latest measured moment
 */

// Score per record: prefer the server-mirrored score (fetchHierarchyData
// annotates _mirroredScore with negative matrix rows flipped within the
// field scale); records without it (stale module cache) fall back to raw
// Input. '' / null / non-numeric must be skipped, NOT coerced to 0
// (Number('') === 0 would poison averages).
const recordScore = (record) => {
    if (record && record._mirroredScore !== undefined) return record._mirroredScore
    const value = record?.Input
    if (value === '' || value == null) return null
    const num = Number(value)
    return Number.isFinite(num) ? num : null
}

/**
 * @param {object[]} records - Response records of one effect across moments
 * @param {object} [options]
 * @param {string} [options.key] - Respondent key field (default 'Persona')
 * @returns {ScatterPoint[]} One point per respondent with ≥2 measured moments
 */
const buildPrePostScatter = (records, { key = 'Persona' } = {}) => {
    // respondent → momentPosition → {sum, count}
    /** @type {Map<string, Map<number, {sum: number, count: number}>>} */
    const byRespondent = new Map()

    ;(Array.isArray(records) ? records : []).forEach((r) => {
        const respondent = r?.[key]
        const score = recordScore(r)
        const moment = r?.MomentPosition
        // Empty-string keys are anonymous responses — they must never pair
        if (!respondent || score === null || !Number.isFinite(moment)) return

        const moments = byRespondent.get(respondent) || new Map()
        const entry = moments.get(moment) || { sum: 0, count: 0 }
        entry.sum += score
        entry.count += 1
        moments.set(moment, entry)
        byRespondent.set(respondent, moments)
    })

    const points = []
    byRespondent.forEach((moments, respondent) => {
        if (moments.size < 2) return
        const positions = [...moments.keys()].sort((a, b) => a - b)
        const first = moments.get(positions[0])
        const latest = moments.get(positions[positions.length - 1])
        points.push({
            respondent,
            x: first.sum / first.count,
            y: latest.sum / latest.count,
        })
    })
    return points
}

export default buildPrePostScatter
